import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getJob, createSession } from "@/lib/api";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { Briefcase, ArrowLeft, Clock, MapPin, Users, AlertCircle, Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import BrandedHeader from "@/components/BrandedHeader";

export default function CandidateJobDetailPage() {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [candidateName, setCandidateName] = useState("");
  const [cvSummary, setCvSummary] = useState("");

  useEffect(() => {
    loadJob();
  }, [jobId]);

  const loadJob = async () => {
    try {
      const res = await getJob(jobId);
      setJob(res.data);
    } catch (e) {
      toast.error("Failed to load job details");
    } finally {
      setLoading(false);
    }
  };

  const handleStartInterview = async () => {
    if (!candidateName.trim()) {
      toast.error("Please enter your name");
      return;
    }
    setStarting(true);
    try {
      const res = await createSession({
        job_id: jobId,
        candidate_name: candidateName.trim(),
        cv_summary: cvSummary.trim() || "Not provided",
        cv_skills_detected: [],
        cv_experience_years: 0,
      });
      navigate(`/interview/${res.data.session_id}`);
    } catch (e) {
      toast.error("Failed to start interview session");
      setStarting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-6 h-6 text-primary animate-spin" />
      </div>
    );
  }

  if (!job) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <p className="text-muted-foreground">Job not found</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <BrandedHeader>
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/jobs')}>
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <Briefcase className="w-6 h-6 text-primary" />
          <span className="font-display text-lg font-semibold">Position Details</span>
        </div>
      </BrandedHeader>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
        <div className="grid lg:grid-cols-[1fr_380px] gap-8">
          {/* Left: Job Details */}
          <div>
            <div className="flex items-center gap-3 mb-2">
              <Badge variant="outline">{job.role_type}</Badge>
              <Badge variant="outline">{job.seniority_level}</Badge>
            </div>
            <h1 className="text-3xl font-display font-semibold mb-3">{job.title}</h1>
            <div className="flex items-center gap-4 text-sm text-muted-foreground mb-6">
              {job.location && (
                <span className="flex items-center gap-1">
                  <MapPin className="w-4 h-4" />
                  {job.location}
                </span>
              )}
              {job.department && (
                <span className="flex items-center gap-1">
                  <Briefcase className="w-4 h-4" />
                  {job.department}
                </span>
              )}
              <span className="flex items-center gap-1">
                <Clock className="w-4 h-4" />
                {job.target_duration_minutes || 30} min interview
              </span>
            </div>

            <Separator className="mb-6" />

            <div className="mb-6">
              <h3 className="font-display font-semibold text-lg mb-3">About this role</h3>
              <p className="text-muted-foreground leading-relaxed whitespace-pre-line">
                {job.description}
              </p>
            </div>

            <div className="mb-6">
              <h3 className="font-display font-semibold text-lg mb-3" data-testid="job-detail-skills-list">Required Skills</h3>
              <div className="flex flex-wrap gap-2">
                {job.required_skills.map((skill, i) => (
                  <Badge key={i} variant="secondary" className="px-3 py-1">{skill}</Badge>
                ))}
              </div>
            </div>

            <div className="bg-card border border-border rounded-xl p-4">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-[hsl(var(--warning))] mt-0.5 flex-shrink-0" />
                <div className="text-sm">
                  <p className="font-medium mb-1">Interview Information</p>
                  <ul className="text-muted-foreground space-y-1">
                    <li>This is an AI-powered interview with 5 structured stages</li>
                    <li>Your webcam will be active for proctoring during the session</li>
                    <li>The interview will take approximately {job.target_duration_minutes || 30} minutes</li>
                    <li>Please ensure you're in a quiet environment with stable internet</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Right: Start Interview */}
          <div className="lg:sticky lg:top-8 self-start">
            <Card className="border-border bg-card">
              <CardHeader className="p-5">
                <CardTitle className="font-display text-lg">Start Your Interview</CardTitle>
              </CardHeader>
              <CardContent className="px-5 pb-5 space-y-4">
                <div>
                  <label className="text-sm font-medium mb-1.5 block">Your Full Name *</label>
                  <Input
                    placeholder="John Doe"
                    value={candidateName}
                    onChange={e => setCandidateName(e.target.value)}
                    className="bg-card"
                    data-testid="candidate-name-input"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium mb-1.5 block">Brief CV Summary (optional)</label>
                  <textarea
                    placeholder="Briefly describe your experience and skills..."
                    value={cvSummary}
                    onChange={e => setCvSummary(e.target.value)}
                    rows={4}
                    className="flex w-full rounded-lg border border-input bg-card px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/60 focus-visible:ring-offset-2 focus-visible:ring-offset-background resize-none"
                    data-testid="candidate-cv-input"
                  />
                </div>
                <Button
                  className="w-full btn-press"
                  size="lg"
                  onClick={handleStartInterview}
                  disabled={starting || !candidateName.trim()}
                  data-testid="job-detail-start-interview-button"
                >
                  {starting ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Starting Interview...</>
                  ) : (
                    <><Users className="w-4 h-4 mr-2" /> Begin Interview</>
                  )}
                </Button>
                <p className="text-xs text-muted-foreground text-center">
                  By starting, you agree to webcam proctoring during the session.
                </p>
              </CardContent>
            </Card>

            {/* Practice Mode nudge — drives bounce-traffic conversion */}
            <button
              type="button"
              onClick={() => navigate(`/practice/${encodeURIComponent(job?.role_type || 'technical')}`)}
              className="mt-3 w-full rounded-lg border border-dashed border-primary/40 bg-primary/5 hover:bg-primary/10 px-4 py-3 text-left transition-colors group"
              data-testid="job-detail-practice-cta"
            >
              <div className="flex items-start gap-2.5">
                <Sparkles className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-primary">Not ready yet? Try a free practice test</p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    ~2 minutes, no record kept — get a feel for the question style before you apply.
                  </p>
                </div>
              </div>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
